import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { input, textarea, select } from '../../utils/designTokens';

/**
 * Step 1: Basic Info
 * - Title, Description, Icon URL, Category, Tags
 */
const MCPServerFormStep1 = ({ formData, updateFormData, categories }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateFormData({ [name]: value });
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    // Parse comma-separated tags
    const tagsArray = tagsString
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    updateFormData({ tags: tagsArray });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-spooky-ghost-white">
          Basic Information
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Let's start with the essential details about your MCP server.
        </p>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="title">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., File System MCP Server"
          className={input}
          maxLength={255}
          required
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {formData.title.length}/255 characters
        </p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="description">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Describe what your MCP server does and what problems it solves..."
          className={textarea}
          rows={4}
          required
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          <FaInfoCircle className="inline mr-1" />
          Provide a clear description of your server's functionality
        </p>
      </div>

      {/* Icon URL */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="iconUrl">
          Icon URL (Optional)
        </label>
        <input
          type="url"
          id="iconUrl"
          name="iconUrl"
          value={formData.iconUrl}
          onChange={handleChange}
          placeholder="https://example.com/icon.png"
          className={input}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          URL to an icon image for your server (PNG, SVG, or JPG)
        </p>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="categoryId">
          Category
        </label>
        <select
          id="categoryId"
          name="categoryId"
          value={formData.categoryId}
          onChange={handleChange}
          className={select}
        >
          <option value="">Select a category (optional)</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.icon} {cat.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Help users find your server by selecting a relevant category
        </p>
      </div>

      {/* Tags */}
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300" htmlFor="tags">
          Tags
        </label>
        <input
          type="text"
          id="tags"
          name="tags"
          value={formData.tags.join(', ')}
          onChange={handleTagsChange}
          placeholder="e.g., filesystem, storage, automation"
          className={input}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          <FaInfoCircle className="inline mr-1" />
          Comma-separated tags to help users discover your server
        </p>
        {formData.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.tags.map((tag, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 rounded-full text-sm font-semibold"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MCPServerFormStep1;
